<template>
  <v-app style="background-color: #f8f9fb">
    <v-dialog v-model="dialogConfirmacao" persistent max-width="480">
      <v-card>
        <v-card-title>Consulta Lemit</v-card-title>
        <v-card-text>
          Deseja consultar este CPF na Lemit?
          A consulta pode consumir crédito do centro de custo selecionado.
        </v-card-text>
        <v-card-actions class="d-flex justify-center pb-4">
          <v-btn color="grey darken-1" text @click="dialogConfirmacao = false">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="consultando"
            @click="confirmarConsulta"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container>
      <v-col cols="12">
        <v-card elevation="0" class="px-5 py-3">
          <v-row align="end">
            <v-col cols="12" sm="6" md="4">
              <span>CPF</span>
              <v-text-field
                v-model="cpf"
                label="000.000.000-00"
                dense
                solo-inverted
                maxlength="14"
                :disabled="consultando"
                @keyup.enter="abrirConfirmacaoConsulta"
                @input="onCpfInput"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <span>Segmento</span>
              <v-select
                v-model="segmento"
                :items="segmentos"
                item-text="text"
                item-value="value"
                dense
                solo-inverted
                :disabled="consultando"
              ></v-select>
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <span>&nbsp;</span>
              <v-btn
                color="#4682B4"
                block
                height="40"
                style="font-size: 14px; font-weight: bold"
                :loading="consultando"
                :disabled="!cpfLimpo || cpfLimpo.length !== 11"
                @click="abrirConfirmacaoConsulta"
              >
                Consultar
                <v-icon right dark>mdi-magnify</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-container>

    <v-container style="background-color: #eee" fluid tag="section">
      <v-alert
        v-if="mensagem"
        :type="tipoMensagem"
        dense
        text
        class="mb-4"
        dismissible
        @input="mensagem = ''"
      >
        {{ mensagem }}
      </v-alert>

      <v-row v-if="consulta">
        <v-col cols="12">
          <base-material-card
            icon="mdi-account-search"
            title="Resultado da consulta"
            color="rgb(71 130 180)"
            style="margin-top: 10px !important; padding-top: 10px !important"
            class="px-5 py-0"
          >
            <div class="d-flex flex-wrap align-center pt-4 pb-2">
              <v-chip
                small
                class="mr-2 mb-2"
                :color="consulta.origem === 'CACHE' ? 'orange' : 'green'"
                text-color="white"
              >
                {{ consulta.origem === 'CACHE' ? 'CACHE' : 'API' }}
              </v-chip>

              <v-chip
                v-if="pessoa && pessoa.falecido"
                small
                class="mr-2 mb-2"
                color="error"
                text-color="white"
              >
                FALECIDO
              </v-chip>

              <v-chip
                v-if="pessoa && pessoa.situacao_cpf"
                small
                class="mr-2 mb-2"
                color="blue-grey"
                text-color="white"
              >
                {{ pessoa.situacao_cpf }}
              </v-chip>

              <span class="grey--text text--darken-1 mb-2">
                Consulta #{{ consulta.idConsulta }}
                <span v-if="consulta.dataConsulta"> · {{ consulta.dataConsulta }}</span>
                <span v-if="consulta.nomeUsuario"> · {{ consulta.nomeUsuario }}</span>
              </span>
            </div>

            <v-alert
              v-if="consultaEhDiaAnterior"
              type="warning"
              dense
              text
              class="mb-4"
            >
              Esta consulta é anterior, feita no dia {{ dataConsultaLemitExibicao }}.
            </v-alert>

            <v-alert
              v-else-if="consulta.origem === 'CACHE'"
              type="info"
              dense
              text
              class="mb-4"
            >
              Dados reutilizados de consulta anterior (sem nova cobrança).
              <span v-if="consulta.dataConsultaLemit">
                Consulta Lemit realizada em {{ consulta.dataConsultaLemit }}.
              </span>
            </v-alert>

            <v-alert
              v-if="consulta.sucesso === false"
              type="error"
              dense
              text
              class="mb-4"
            >
              {{ consulta.mensagemErro || 'Consulta não retornou dados.' }}
            </v-alert>

            <template v-if="pessoa">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="section-title">Dados pessoais</div>
                  <v-simple-table dense class="elevation-0 mb-4">
                    <tbody>
                      <tr>
                        <td class="label-cell">Nome</td>
                        <td>{{ pessoa.nome || '—' }}</td>
                      </tr>
                      <tr>
                        <td class="label-cell">CPF</td>
                        <td>{{ formatarCpfExibicao(pessoa.cpf || consulta.cpf) }}</td>
                      </tr>
                      <tr>
                        <td class="label-cell">Nascimento</td>
                        <td>{{ formatarDataIso(pessoa.data_nascimento) }}</td>
                      </tr>
                      <tr>
                        <td class="label-cell">Sexo</td>
                        <td>{{ pessoa.sexo || '—' }}</td>
                      </tr>
                      <tr>
                        <td class="label-cell">Nome da mãe</td>
                        <td>{{ pessoa.nome_mae || '—' }}</td>
                      </tr>
                      <tr>
                        <td class="label-cell">Renda</td>
                        <td>{{ formatarMoeda(pessoa.renda) }}</td>
                      </tr>
                      <tr>
                        <td class="label-cell">Ocupação</td>
                        <td>{{ pessoa.ocupacao || '—' }}</td>
                      </tr>
                      <tr v-if="pessoa.risco_credito && pessoa.risco_credito.score_credito">
                        <td class="label-cell">Score crédito</td>
                        <td>{{ pessoa.risco_credito.score_credito }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="section-title">Celulares</div>
                  <v-data-table
                    :headers="headersCelulares"
                    :items="listaOrdenada(pessoa.celulares)"
                    :items-per-page="5"
                    dense
                    class="elevation-0 mb-4"
                    hide-default-footer
                  >
                    <template v-slot:item.numero="{ item }">
                      ({{ item.ddd }}) {{ item.numero }}
                    </template>
                    <template v-slot:item.whatsapp="{ item }">
                      <v-icon v-if="item.whatsapp" small color="green">mdi-whatsapp</v-icon>
                      <span v-else class="grey--text">—</span>
                    </template>
                    <template v-slot:no-data>
                      <div class="text-center py-3">Nenhum celular</div>
                    </template>
                  </v-data-table>

                  <div class="section-title">E-mails</div>
                  <v-data-table
                    :headers="headersEmails"
                    :items="listaOrdenada(pessoa.emails)"
                    :items-per-page="5"
                    dense
                    class="elevation-0 mb-4"
                    hide-default-footer
                  >
                    <template v-slot:no-data>
                      <div class="text-center py-3">Nenhum e-mail</div>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>

              <div class="section-title">Endereços</div>
              <v-data-table
                :headers="headersEnderecos"
                :items="listaOrdenada(pessoa.enderecos)"
                :items-per-page="5"
                dense
                class="elevation-0 mb-4"
              >
                <template v-slot:item.cep="{ item }">
                  {{ formatarCep(item.cep) }}
                </template>
                <template v-slot:no-data>
                  <div class="text-center py-3">Nenhum endereço</div>
                </template>
              </v-data-table>

              <div class="section-title">Telefones fixos</div>
              <v-data-table
                :headers="headersFixos"
                :items="listaOrdenada(pessoa.fixos)"
                :items-per-page="5"
                dense
                class="elevation-0 mb-2"
              >
                <template v-slot:item.numero="{ item }">
                  ({{ item.ddd }}) {{ item.numero }}
                </template>
                <template v-slot:no-data>
                  <div class="text-center py-3">Nenhum telefone fixo</div>
                </template>
              </v-data-table>
            </template>

            <br />
          </base-material-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <base-material-card
            icon="mdi-history"
            title="Histórico do CPF"
            color="rgb(71 130 180)"
            style="margin-top: 20px !important; padding-top: 10px !important"
            class="px-5 py-0"
          >
            <br />
            <v-data-table
              :headers="headersHistorico"
              :items="historico"
              :items-per-page="10"
              :loading="carregandoHistorico"
              loading-text="Carregando histórico..."
              items-per-page-text="Linhas por página"
              class="elevation-0"
              item-key="id"
            >
              <template v-slot:item.cpf="{ item }">
                {{ formatarCpfMascarado(item.cpf) }}
              </template>
              <template v-slot:item.origem="{ item }">
                <v-chip
                  x-small
                  :color="item.origem === 'CACHE' ? 'orange' : 'green'"
                  text-color="white"
                >
                  {{ item.origem || '—' }}
                </v-chip>
              </template>
              <template v-slot:item.sucesso="{ item }">
                <v-chip
                  v-if="item.sucesso === true"
                  x-small
                  color="success"
                  text-color="white"
                >
                  Sim
                </v-chip>
                <v-chip
                  v-else-if="item.sucesso === false"
                  x-small
                  color="error"
                  text-color="white"
                >
                  Não
                </v-chip>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:item.acoes="{ item }">
                <v-btn
                  icon
                  small
                  color="primary"
                  :loading="carregandoDetalheId === item.id"
                  @click="abrirDetalhe(item.id)"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <div class="text-center py-4">
                  <p>
                    {{
                      cpfLimpo.length === 11
                        ? 'Nenhuma consulta encontrada para este CPF.'
                        : 'Informe um CPF e consulte para ver o histórico.'
                    }}
                  </p>
                </div>
              </template>
            </v-data-table>
            <br />
          </base-material-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import LemitService from '../../services/lemit'

const SEGMENTOS = [
  { value: 'corporativo', text: 'Corporativo' },
  { value: 'cobranca', text: 'Cobrança' },
  { value: 'vendas', text: 'Vendas' },
  { value: 'sac', text: 'SAC' },
]

export default {
  name: 'CentralConsultasAnalise',

  data() {
    return {
      cpf: '',
      segmento: 'sac',
      segmentos: SEGMENTOS,
      dialogConfirmacao: false,
      consultando: false,
      carregandoHistorico: false,
      carregandoDetalheId: null,
      mensagem: '',
      tipoMensagem: 'info',
      consulta: null,
      historico: [],
      headersCelulares: [
        { text: 'Ranking', value: 'ranking', width: '90px' },
        { text: 'Número', value: 'numero' },
        { text: 'WhatsApp', value: 'whatsapp', width: '100px' },
      ],
      headersFixos: [
        { text: 'Ranking', value: 'ranking', width: '90px' },
        { text: 'Número', value: 'numero' },
      ],
      headersEmails: [
        { text: 'Ranking', value: 'ranking', width: '90px' },
        { text: 'E-mail', value: 'email' },
      ],
      headersEnderecos: [
        { text: 'Ranking', value: 'ranking', width: '90px' },
        { text: 'Endereço', value: 'endereco' },
        { text: 'Bairro', value: 'bairro' },
        { text: 'Cidade', value: 'cidade' },
        { text: 'UF', value: 'uf', width: '60px' },
        { text: 'CEP', value: 'cep', width: '110px' },
        { text: 'Tipo', value: 'tipo', width: '110px' },
      ],
      headersHistorico: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: 'CPF', value: 'cpf', width: '140px' },
        { text: 'Data', value: 'dataConsulta' },
        { text: 'Usuário', value: 'nomeUsuario' },
        { text: 'Origem', value: 'origem', width: '100px' },
        { text: 'Sucesso', value: 'sucesso', width: '100px' },
        { text: 'Ações', value: 'acoes', sortable: false, width: '80px' },
      ],
    }
  },

  computed: {
    cpfLimpo() {
      return String(this.cpf || '').replace(/\D/g, '')
    },
    pessoa() {
      return this.consulta?.dados?.pessoa || null
    },
    dataConsultaLemitReferencia() {
      return this.obterDataConsultaLemit(this.consulta)
    },
    consultaEhDiaAnterior() {
      return this.isDiaAnterior(this.dataConsultaLemitReferencia)
    },
    dataConsultaLemitExibicao() {
      if (!this.dataConsultaLemitReferencia) return ''
      return this.dataConsultaLemitReferencia.toLocaleDateString('pt-BR')
    },
  },

  methods: {
    onCpfInput() {
      const digits = this.cpfLimpo.slice(0, 11)
      let masked = digits
      if (digits.length > 9) {
        masked = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`
      } else if (digits.length > 6) {
        masked = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`
      } else if (digits.length > 3) {
        masked = `${digits.slice(0, 3)}.${digits.slice(3)}`
      }
      this.cpf = masked
    },

    formatarCpfExibicao(cpf) {
      const digits = String(cpf || '').replace(/\D/g, '')
      if (digits.length !== 11) return cpf || '—'
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`
    },

    formatarCpfMascarado(cpf) {
      const digits = String(cpf || '').replace(/\D/g, '')
      if (digits.length !== 11) return cpf || '—'
      return `***.***.${digits.slice(6, 9)}-${digits.slice(9)}`
    },

    formatarCep(cep) {
      const digits = String(cep || '').replace(/\D/g, '')
      if (digits.length !== 8) return cep || '—'
      return `${digits.slice(0, 5)}-${digits.slice(5)}`
    },

    formatarDataIso(value) {
      if (!value) return '—'
      const data = new Date(value)
      if (Number.isNaN(data.getTime())) return value
      return data.toLocaleDateString('pt-BR')
    },

    formatarMoeda(valor) {
      if (valor === null || valor === undefined || valor === '') return '—'
      const numero = Number(valor)
      if (Number.isNaN(numero)) return String(valor)
      return numero.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },

    listaOrdenada(lista) {
      if (!Array.isArray(lista)) return []
      return [...lista].sort((a, b) => (a.ranking || 999) - (b.ranking || 999))
    },

    obterDataConsultaLemit(consulta) {
      if (!consulta) return null
      return (
        this.parseDataLemit(consulta.dataConsultaLemit) ||
        this.parseDataLemit(consulta.dados?.data_consulta)
      )
    },

    parseDataLemit(valor) {
      if (!valor) return null

      const texto = String(valor).trim()
      const brMatch = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})/)
      if (brMatch) {
        const [, day, month, year] = brMatch
        const data = new Date(Number(year), Number(month) - 1, Number(day))
        return Number.isNaN(data.getTime()) ? null : data
      }

      const data = new Date(texto)
      return Number.isNaN(data.getTime()) ? null : data
    },

    isDiaAnterior(data) {
      if (!data) return false

      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)

      const referencia = new Date(data)
      referencia.setHours(0, 0, 0, 0)

      return referencia.getTime() < hoje.getTime()
    },

    extrairMensagemErro(error) {
      const data = error?.response?.data
      if (!data) return 'Não foi possível consultar a Lemit. Tente novamente.'
      if (typeof data === 'string') return data
      return (
        data.mensagem ||
        data.mensagemErro ||
        data.message ||
        'Não foi possível consultar a Lemit. Tente novamente.'
      )
    },

    async carregarHistorico() {
      if (this.cpfLimpo.length !== 11) {
        this.historico = []
        return
      }

      this.carregandoHistorico = true
      try {
        const response = await LemitService.historicoPorCpf(this.cpfLimpo)
        this.historico = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Erro ao carregar histórico Lemit:', error)
        this.historico = []
      } finally {
        this.carregandoHistorico = false
      }
    },

    abrirConfirmacaoConsulta() {
      if (this.cpfLimpo.length !== 11) {
        this.mensagem = 'Informe um CPF válido com 11 dígitos.'
        this.tipoMensagem = 'warning'
        return
      }

      this.dialogConfirmacao = true
    },

    async confirmarConsulta() {
      this.dialogConfirmacao = false
      await this.consultar()
    },

    async consultar() {
      if (this.cpfLimpo.length !== 11) {
        this.mensagem = 'Informe um CPF válido com 11 dígitos.'
        this.tipoMensagem = 'warning'
        return
      }

      this.consultando = true
      this.mensagem = ''

      try {
        const response = await LemitService.consultarPessoa(
          this.cpfLimpo,
          this.segmento,
          false
        )
        this.consulta = response.data || null

        if (this.consulta?.sucesso === false) {
          this.mensagem =
            this.consulta.mensagemErro || 'Consulta concluída sem sucesso.'
          this.tipoMensagem = 'error'
        } else {
          this.mensagem =
            this.consulta?.origem === 'CACHE'
              ? 'Consulta concluída com dados em cache.'
              : 'Consulta concluída com sucesso.'
          this.tipoMensagem = 'success'
        }

        await this.carregarHistorico()
      } catch (error) {
        console.error('Erro ao consultar Lemit:', error)
        this.consulta = error?.response?.data?.dados !== undefined
          ? error.response.data
          : this.consulta
        this.mensagem = this.extrairMensagemErro(error)
        this.tipoMensagem = 'error'
        await this.carregarHistorico()
      } finally {
        this.consultando = false
      }
    },

    async abrirDetalhe(id) {
      this.carregandoDetalheId = id
      this.mensagem = ''

      try {
        const response = await LemitService.detalheConsulta(id)
        this.consulta = response.data || null
        this.mensagem = `Detalhe da consulta #${id} carregado.`
        this.tipoMensagem = 'info'
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (error) {
        console.error('Erro ao carregar detalhe Lemit:', error)
        this.mensagem = this.extrairMensagemErro(error)
        this.tipoMensagem = 'error'
      } finally {
        this.carregandoDetalheId = null
      }
    },
  },
}
</script>

<style scoped>
.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #355c7d;
  margin: 8px 0 10px;
}

.label-cell {
  width: 140px;
  font-weight: 600;
  color: #546e7a;
}
</style>
